import React from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { usePurchaseOrderPdfActions } from '../../hooks/usePurchaseOrderPdfActions';
import { Icon } from '@shared/icons';
import { IconSize } from '@shared/icons/Icon';
import { useTheme } from '@mui/material/styles';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { CreatePurchaseOrderFlow } from './CreatePurchaseOrderFlow';

export interface OrderPlacedDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseOrderId: string;
  poNumber?: string;
  supplierEmail?: string;
  supplierName?: string;
  supplierPhone?: string;
  supplierWebsite?: string;
  supplierAddressLine?: string;
}


export const OrderPlacedDialog: React.FC<OrderPlacedDialogProps> = ({
  open,
  onClose,
  purchaseOrderId,
  poNumber,
  supplierEmail,
  supplierName,
  supplierPhone,
  supplierWebsite,
  supplierAddressLine,
}) => {

  const { openBottomSheet, closeBottomSheet } = useBottomSheet();

  const theme = useTheme();

  const { isPdfBusy, downloadOrderPdf } = usePurchaseOrderPdfActions({
    purchaseOrderId,
    poNumber,
  });

  const handleContinueWithoutSending = () => {
    onClose();
  };

  const handleDownloadPdf = async () => {
    await downloadOrderPdf();
  };

  const handleCreateAnotherPurchaseOrder = () => {
    openBottomSheet({
      title: 'Create another purchase order',
      component: <CreatePurchaseOrderFlow handleClose={async () => { await closeBottomSheet(); }} />,
    });
    onClose();
  };

  const hasSupplier = Boolean(supplierName?.trim());

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          py: 2.5,
          px: 3,
          pb: 2,
        }}
      >
        <Typography variant="h6">
          Don&apos;t forget to send your order
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"

        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent>
        <Stack gap={2}>

          {hasSupplier ? (

            <Stack gap={1}>
              <Typography variant="h6">
                {(supplierName ?? '').trim() || 'No supplier'}
              </Typography>

              <Stack gap={2} direction="row" alignItems="center" flexWrap="wrap" justifyContent="flex-start">


                {supplierEmail && (
                  <Stack direction="row" gap={1} alignItems="center">
                    <Icon icon="Mail01" color={theme.palette.text.secondary} size={IconSize.MEDIUM} />
                    <Typography variant="body2" color="text.secondary">
                      {supplierEmail}
                    </Typography>
                  </Stack>
                )}

                {supplierPhone && (
                  <Stack direction="row" gap={1} alignItems="center">
                    <Icon icon="Phone" color={theme.palette.text.secondary} size={IconSize.MEDIUM} />
                    <Typography variant="body2" color="text.secondary">
                      {supplierPhone}
                    </Typography>
                  </Stack>
                )}

                {supplierWebsite && (
                  <Stack direction="row" gap={1} alignItems="center">
                    <Icon icon="Globe01" color={theme.palette.text.secondary} size={IconSize.MEDIUM} />
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {supplierWebsite}
                    </Typography>
                  </Stack>
                )}

                {supplierAddressLine && (
                  <Stack direction="row" gap={1} alignItems="center">
                    <Icon icon="Map01" color={theme.palette.text.secondary} size={IconSize.MEDIUM} />
                    <Typography variant="body2" color="text.secondary">
                      {supplierAddressLine}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>

          ) : (
            <Stack gap={1} alignItems="center" direction="row">

              <Icon icon="AlertCircle" size={IconSize.LARGE} />

              <Typography variant="body1">
                This purchase order is not linked to a supplier
              </Typography>
            </Stack>
          )}

          <Divider />

          <Button
            sx={{
              alignSelf: 'flex-start',
            }}

            variant="text"
            color="primary"
            size="large"
            startIcon={isPdfBusy ? <CircularProgress size={20} color="inherit" /> : <Icon icon="Download01" />}
            disabled={isPdfBusy}
            onClick={() => void handleDownloadPdf()}
          >
            Download PDF
          </Button>

        </Stack>


      </DialogContent>


      <DialogActions sx={{ py: 2, px: 3, borderTop: 1, borderColor: 'divider' }}>
        <Button startIcon={<Icon icon="Plus01" />} variant="text" color="primary" size="large" onClick={handleCreateAnotherPurchaseOrder}>
          Create another order
        </Button>
        <Button variant="contained" color="primary" size="large" onClick={handleContinueWithoutSending}>
          Finish
        </Button>
      </DialogActions>
    </Dialog >

  );
};
